import {
  CacheContext,
  CacheDataSet,
  CacheExtentMode,
  CacheProgress,
  CacheStorageAdapter,
  CacheStorageDataSet,
  DataEntry,
  QueryObjectsModel,
} from '@barthauer/abc/core/data';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import { concatMap, map, tap } from 'rxjs/operators';
import { MapLoader } from './map.loader';
import { DataEntity, DataEntityInfo } from './models/element.model';
import { ObjectTypesHelper } from './objecttypes-helper';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';

declare let ol: any;
declare let proj4: any;

export class MapQueryCache {
  private static readonly cacheDataSetName = 'OpenLayersQueries';
  private static readonly tableNameInfos = '#infos';
  private static readonly tableNameEntities = '#entities';
  private static readonly tableNameNetwork = '#network';
  private static readonly tableNameColors = '#colors';
  private static readonly keyTileSizes = '#tileSizes';
  private cacheDataSet: CacheDataSet;
  private readonly DefaultTileSizes: number[] = [50000, 5000, 500];

  constructor(
    private mapLoader: MapLoader,
    private cacheStorage: CacheStorageAdapter,
    private olmap: any,
    private initialExtent: number[],
    private sources: Map<string, any>
  ) {}

  private getExtentFromContext(ctx: CacheContext) {
    const extentOption = ctx.extentMode;
    if (extentOption === CacheExtentMode.CurrentView) return this.olmap.getView().calculateExtent();
    else if (extentOption === CacheExtentMode.InitialView) return this.initialExtent;
    else if (extentOption === CacheExtentMode.UserExtent) return ctx.selectedExtent;
  }

  public buildExtentFilterFromContext(ctx: CacheContext, parameters: ParameterValue[]): string {
    const extent = this.mapLoader.prepareObjectSpaceExtent(this.getExtentFromContext(ctx));
    return this.mapLoader.buildExtentFilter(extent, this.mapLoader.currentCacheFilterExpression, parameters);
  }

  public applyExtentFilterFromContext(ctx: CacheContext, query: QueryObjectsModel): QueryObjectsModel {
    const extent = this.mapLoader.prepareObjectSpaceExtent(this.getExtentFromContext(ctx));
    return this.mapLoader.prepareObjectSpaceQueryWithExtent(query, extent, this.mapLoader.currentCacheFilterExpression);
  }

  private downloadFeaturesToCache(ctx: CacheContext, item: CacheDataSet): Observable<CacheProgress> {
    const extent = this.getExtentFromContext(ctx);
    const osextent = this.mapLoader.prepareObjectSpaceExtent(extent);
    const progress = new Subject<CacheProgress>();

    let errorCount = 0;
    let stepCount = 0;
    const sourcesCount = this.sources.size;
    const maxCount = sourcesCount * 2;

    ctx.storage
      .createStorageDataSet(item, {
        extent: extent,
        tileSizes: this.DefaultTileSizes,
      })
      .then((_) => {
        of(Array.from(this.sources))
          .pipe(
            concatMap((sources) => {
              const objectTypes = new Map<string, Map<string, DataEntity>>();
              const observables: Observable<any>[] = [];
              for (const e of sources) {
                const tableName = e[0];
                const olsource = e[1];
                const query = olsource.layerdefinition.query;
                observables.push(
                  this.mapLoader
                    .queryObjectSpaceObjectsByExtent(query, osextent, this.mapLoader.currentCacheFilterExpression)
                    .pipe(
                      concatMap((data) => {
                        const features = this.mapLoader.readFeatures(
                          data,
                          null,
                          null,
                          olsource.layerdefinition.identityColumnName
                        );
                        let tiles: Map<string, string[]> = new Map<string, string[]>();
                        let objectIds: Map<string, string[]> = new Map<string, string[]>();
                        const arrFeatures: { key: string; value: any }[] = [];
                        if (features) {
                          const featureCount = features.length;
                          let featureStepCount = 0;
                          for (let i = 0; i < featureCount; i++) {
                            const f = features[i];
                            const json = data.features[i];
                            const geoextent = f.getGeometry()?.getExtent();
                            if (osextent && geoextent) {
                              const minTileSize = this.DefaultTileSizes[this.DefaultTileSizes.length - 1];
                              if (geoextent[0] < osextent[0] - minTileSize) {
                                geoextent[0] = osextent[0] - minTileSize;
                              }
                              if (geoextent[1] < osextent[1] - minTileSize) {
                                geoextent[1] = osextent[1] - minTileSize;
                              }
                              if (geoextent[2] > osextent[2] + minTileSize) {
                                geoextent[2] = osextent[2] + minTileSize;
                              }
                              if (geoextent[3] > osextent[3] + minTileSize) {
                                geoextent[3] = osextent[3] + minTileSize;
                              }
                            }
                            const id = f.getId();
                            const oId = MapLoader.getFeatureObjectId(f);
                            if (id && oId) {
                              let arrIds: string[];
                              if (objectIds.has(oId)) {
                                arrIds = objectIds.get(oId);
                              } else {
                                arrIds = [];
                              }
                              arrIds.push(id);
                              objectIds.set(oId, arrIds);
                            }
                            if (geoextent && id) {
                              for (let tile of this.getTileCoordKeysFromExtent(
                                geoextent,
                                true,
                                this.DefaultTileSizes
                              )) {
                                if (ctx.checkCancelled(progress)) {
                                  return of(null);
                                }
                                let ids = tiles.get(tile) ?? [];
                                ids.push(id);
                                tiles.set(tile, ids);
                                progress.next(
                                  new CacheProgress(stepCount / maxCount + featureStepCount / featureCount / maxCount)
                                );
                              }
                              arrFeatures.push({ key: id, value: json });
                              featureStepCount++;
                            }

                            const e = MapLoader.toDataEntity(f, true);
                            if (e) {
                              let dataEntities = objectTypes.get(e.objectType.toString());
                              if (!dataEntities) {
                                dataEntities = new Map<string, DataEntity>();
                                objectTypes.set(e.objectType.toString(), dataEntities);
                              }
                              dataEntities.set(e.id, e);
                            }
                          }
                        }

                        const arrTiles: { key: string; value: any }[] = [];
                        for (let e of Array.from(tiles)) {
                          const key = e[0];
                          const value = e[1];
                          arrTiles.push({ key: key, value: value });
                        }
                        const arrObjectIds: { key: string; value: any }[] = [];
                        for (let e of Array.from(objectIds)) {
                          const key = e[0];
                          const value = e[1];
                          arrObjectIds.push({ key: key, value: value });
                        }
                        if (ctx.checkCancelled(progress)) {
                          return of(null);
                        }
                        return forkJoin([
                          from(_.set(MapQueryCache.keyTileSizes, this.DefaultTileSizes, tableName)),
                          from(this.saveToCache(_, arrFeatures, tableName)),
                          from(this.saveToCache(_, arrTiles, tableName)),
                          from(this.mergeArrayToCache(_, arrObjectIds, MapQueryCache.tableNameEntities)),
                        ]).pipe(
                          tap(() => {
                            progress.next(new CacheProgress(++stepCount / maxCount));
                          })
                        );
                      })
                    )
                );
              }
              return forkJoin(observables)
                .pipe(map((_) => objectTypes))
                .pipe(
                  tap((_) => {
                    for (const e of sources) {
                      const olsource = e[1];
                      try {
                        olsource.refresh();
                      } catch {}
                    }
                  })
                );
            })
          )
          .pipe(
            concatMap((objectTypes) => {
              const observables: Observable<string>[] = [];
              let objectTypesStepCount = 0;
              for (const t of Array.from(objectTypes)) {
                const ot = t[0];
                const dataEntities = t[1];
                observables.push(
                  this.mapLoader.queryDataEntityInfos(Array.from(dataEntities.values())).pipe(
                    concatMap((infos) => {
                      const data: { key: string; value: any }[] = [];
                      if (Array.isArray(infos)) {
                        for (let info of infos) {
                          data.push({ key: info.entity.id, value: info });
                        }
                      }
                      return from(_.setData(data, MapQueryCache.tableNameInfos)).pipe(
                        tap(() => {
                          progress.next(
                            new CacheProgress(
                              ((1 + ++objectTypesStepCount / objectTypes.size) * sourcesCount) / maxCount
                            )
                          );
                        }),
                        map(() => {
                          return ot;
                        })
                      );
                    })
                  )
                );
              }
              return forkJoin(observables);
            }),
            concatMap((objectTypes) => {
              const mainEdgeObbjectTypes = ObjectTypesHelper.getMainEdgeObjectTypes();
              const observables: Observable<void>[] = [];
              const visited = {};
              for (const ot of objectTypes) {
                for (const otEdge of mainEdgeObbjectTypes) {
                  observables.push(
                    from(this.mapLoader.mapSelection.isOfType(ot, otEdge)).pipe(
                      concatMap((isEdge) => {
                        if (isEdge && !visited[otEdge]) {
                          visited[otEdge] = true;
                          return this.mapLoader.queryNetworkData(otEdge).pipe(
                            concatMap((rows) => {
                              const data: { key: string; value: any }[] = [];
                              if (Array.isArray(rows)) {
                                const objectsMap = new Map<string, string[]>();
                                let index = 0;
                                for (const row of rows) {
                                  const key = otEdge + '_' + (index++).toString();
                                  data.push({ key: key, value: row });
                                  for (const id of [row['Id'], row['StartNodeId'], row['EndNodeId']]) {
                                    if (id) {
                                      let arr = objectsMap.get(id);
                                      if (!arr) {
                                        arr = [];
                                        objectsMap.set(id, arr);
                                      }
                                      arr.push(key);
                                    }
                                  }
                                }
                                for (const obj of Array.from(objectsMap)) {
                                  data.push({ key: obj[0], value: obj[1] });
                                }
                              }
                              return from(_.setData(data, MapQueryCache.tableNameNetwork));
                            })
                          );
                        }
                        return of(null);
                      })
                    )
                  );
                }
              }
              return forkJoin(observables);
            })
          )
          .subscribe(
            () => {},
            (err) => {
              progress.next(
                new CacheProgress(
                  stepCount / maxCount,
                  ++errorCount / maxCount,
                  this.cacheStorage.error.getFullErrorMessage(err)
                )
              );
            }
          );
      });

    return CacheProgress.toObservable(progress);
  }

  private async saveToCache(
    item: CacheStorageDataSet,
    data: { key: string; value: any }[],
    tableName: string
  ): Promise<void> {
    await item.setData(data, tableName);
  }

  private async mergeArrayToCache(
    item: CacheStorageDataSet,
    data: { key: string; value: unknown[] }[],
    tableName: string
  ): Promise<void> {
    await item.mergeArrayData(data, tableName);
  }

  public createMapCacheItem(): CacheDataSet {
    const dataSet: CacheDataSet = new CacheDataSet(MapQueryCache.cacheDataSetName);

    const tableNames = Array.from(this.sources.keys());
    tableNames.push(MapQueryCache.tableNameInfos);
    tableNames.push(MapQueryCache.tableNameEntities);
    tableNames.push(MapQueryCache.tableNameNetwork);
    tableNames.push(MapQueryCache.tableNameColors);
    dataSet.tableNames = tableNames;
    dataSet.cacheMethod = (ctx) => this.downloadFeaturesToCache(ctx, dataSet);

    this.cacheDataSet = dataSet;

    return dataSet;
  }

  public async tryLoadFeatures(tableName: string, extent: number[]): Promise<any> {
    const storage = await this.cacheStorage.getStorageDataSet(this.cacheDataSet);
    if (storage) {
      return await this.getFeaturesFromCache(storage, tableName, extent);
    }
    return null;
  }

  private async getFeaturesFromCache(storage: CacheStorageDataSet, tableName: string, extent: number[]): Promise<any> {
    const ids = await this.getIdsFromCache(storage, tableName, extent);
    if (ids) return this.getFeaturesByIdFromCache(storage, tableName, ids);
  }

  private async getFeaturesByIdFromCache(storage: CacheStorageDataSet, tableName: string, ids: string[]): Promise<any> {
    let result: any = {
      type: 'FeatureCollection',
      features: [],
    };
    const arr = await storage?.getData(ids, tableName);
    for (const f of arr) {
      if (f) {
        result.features.push(f);
      }
    }
    return result;
  }

  private async getIdsFromCache(storage: CacheStorageDataSet, tableName: string, extent: number[]): Promise<string[]> {
    const result: string[] = [];
    const visited = {};
    let tileSizes: number[] = await storage.get(MapQueryCache.keyTileSizes, tableName);
    if (!tileSizes?.length) {
      tileSizes = [(await storage.loadMetaData())['tileSize']];
    }
    const idsArray = await storage?.getData(this.getTileCoordKeysFromExtent(extent, false, tileSizes), tableName);
    for (const ids of idsArray) {
      if (ids) {
        for (const id of ids) {
          if (!visited[id]) {
            result.push(id);
            visited[id] = true;
          }
        }
      }
    }
    return result;
  }

  private async getIdsFromCacheForEntities(storage: CacheStorageDataSet, entities: DataEntity[]): Promise<string[]> {
    const result: string[] = [];
    for (const e of entities) {
      const ids = (await storage?.get(e.id, MapQueryCache.tableNameEntities)) as string[];
      if (ids) {
        result.push(...ids);
      }
    }
    return result;
  }

  private getTileCoordKeysFromExtent(extent: number[], allLevels: boolean, storedTileSizes: number[]): string[] {
    const result: string[] = [];
    const tileSizes: number[] = [];
    if (allLevels) {
      tileSizes.push(...storedTileSizes);
    } else {
      for (const tileSize of storedTileSizes) {
        if (extent[2] - extent[0] > tileSize || extent[3] - extent[1] > tileSize) {
          tileSizes.push(tileSize);
          break;
        }
      }
      if (!tileSizes.length) {
        tileSizes.push(storedTileSizes[storedTileSizes.length - 1]);
      }
    }

    const left = extent[0];
    const bottom = extent[1];
    const right = extent[2];
    const top = extent[3];

    for (const tileSize of tileSizes) {
      let xstart = left - (left % tileSize);
      if (left < 0.0) {
        xstart -= tileSize;
      }
      let ystart = bottom - (bottom % tileSize);
      if (bottom < 0.0) {
        ystart -= tileSize;
      }
      for (let x = xstart; x <= right; x += tileSize) {
        for (let y = ystart; y <= top; y += tileSize) {
          result.push(this.getTileCoordKey([x, y], tileSize, storedTileSizes));
        }
      }
    }
    return result;
  }

  private getTileCoordKey(tileCoord: number[], tileSize: number, storedTileSizes: number[]): string {
    let key = tileCoord[0] + '_' + tileCoord[1];
    if (tileSize === storedTileSizes[storedTileSizes.length - 1]) {
      return key;
    } else {
      return key + '#' + tileSize;
    }
  }

  public async tryLoadDataEntityInfos(entities: DataEntity[]): Promise<DataEntityInfo[]> {
    const storage = await this.cacheStorage.getStorageDataSet(this.cacheDataSet);
    if (storage) {
      const result: DataEntityInfo[] = [];
      for (let e of entities) {
        const info = await storage.get(e.id, MapQueryCache.tableNameInfos);
        if (info) {
          result.push(info);
        }
      }
      return result;
    }
    return null;
  }

  public async tryLoadFeaturesForEntities(tableName: string, entites: DataEntity[]): Promise<any> {
    const storage = await this.cacheStorage.getStorageDataSet(this.cacheDataSet);
    if (storage && entites) {
      return await this.getFeaturesByIdFromCache(
        storage,
        tableName,
        await this.getIdsFromCacheForEntities(storage, entites)
      );
    }
    return null;
  }

  public async saveFeaturesById(
    tableName: string,
    data: { key: string; value: any }[],
    entities: DataEntity[]
  ): Promise<void> {
    const storage = await this.cacheStorage.getStorageDataSet(this.cacheDataSet);
    if (storage && data && entities) {
      const ids = await this.getIdsFromCacheForEntities(storage, entities);
      const dataToSave: DataEntry[] = [];
      for (const entry of data) {
        if (ids.indexOf(entry.key) >= 0) {
          dataToSave.push(entry);
        }
      }
      await storage?.setData(dataToSave, tableName);
    }
  }

  public async tryLoadNetworkItem(key: string): Promise<any> {
    const storage = await this.cacheStorage.getStorageDataSet(this.cacheDataSet);
    if (storage && key) {
      return await storage.get(key, MapQueryCache.tableNameNetwork);
    }
    return null;
  }

  public async tryDeleteColors(): Promise<any> {
    const storage = await this.cacheStorage.getStorageDataSet(this.cacheDataSet);
    if (storage) {
      return await storage.processAllValues(async () => {}, true, MapQueryCache.tableNameColors);
    }
    return null;
  }

  public async trySaveColor(key: string, color: number[]): Promise<any> {
    const storage = await this.cacheStorage.getStorageDataSet(this.cacheDataSet);
    if (storage) {
      return await storage.set(key, color, MapQueryCache.tableNameColors);
    }
    return null;
  }

  public async tryLoadColors(action: (key: string, color: number[]) => void): Promise<void> {
    const storage = await this.cacheStorage.getStorageDataSet(this.cacheDataSet);
    if (storage) {
      await storage.processAllEntries(
        (key, value) => {
          action(key, value);
          return value;
        },
        false,
        MapQueryCache.tableNameColors
      );
    }
    return null;
  }
}
